import { useState } from 'react';
import { Control, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { collectFormErrors } from '../../../../utils/collect-form-errors';
import { Button } from '../../../atoms/button/button';
import { FormError } from '../../../atoms/form-error/form-error';
import { Header } from '../../../atoms/header/header';
import { Text } from '../../../atoms/text/text';
import { createSubmitHandler } from '../handle-submit';

import { Checkboxes } from './checkboxes';
import { FormFields } from './register-form-fields';
import { Inputs, RegistrationMethod } from './types';

const FormActions = ({
  control,
  errors,
  formErrors,
  isSubmitting,
  onUrgentHelpClick,
  preSignUpText,
}: {
  readonly control: Control<Inputs>;
  readonly errors: Partial<Record<keyof Inputs, { message?: string }>>;
  readonly formErrors: string[];
  readonly isSubmitting: boolean;
  readonly onUrgentHelpClick: () => void;
  readonly preSignUpText?: { text: string; title: string };
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-7.5 mt-7.5">
      <Checkboxes control={control} errors={errors} onUrgentHelpClick={onUrgentHelpClick} />
      <FormError error={formErrors.length > 0 ? formErrors : null} icon="alert-circle" />
      <Button className="w-full" loading={isSubmitting}>
        {t('auth.register.startSurveyButtonText')}
      </Button>
      {preSignUpText && (
        <div className="flex flex-col gap-2 mb-6">
          {preSignUpText.title && <Header level="h4">{preSignUpText.title}</Header>}
          {preSignUpText.text && <Text size="sm">{preSignUpText.text}</Text>}
        </div>
      )}
    </div>
  );
};

type OnSubmit = ({ username, password }: { username: string; password: string }) => void;

type RegisterFormProps = {
  readonly onSubmit: OnSubmit;
  readonly onUrgentHelpClick: () => void;
  readonly preSignUpText?: { title: string; text: string };
  readonly passwordLess?: boolean;
  readonly signInUrl?: string;
  readonly defaultMethod?: RegistrationMethod;
  readonly allowToggle?: boolean;
};
const RegisterForm = ({
  onSubmit,
  onUrgentHelpClick,
  preSignUpText,
  passwordLess = false,
  signInUrl,
  defaultMethod,
  allowToggle,
}: RegisterFormProps) => {
  const {
    register,
    handleSubmit,
    control,
    clearErrors,
    setError,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<Inputs>();
  const watchPassword = watch('password');
  const [showPassword, setShowPassword] = useState<boolean>();
  const passwordInputType = showPassword ? 'text' : 'password';
  const submitHandler = handleSubmit(createSubmitHandler({ onSubmit, setError }));
  const { t } = useTranslation();
  const formErrors = collectFormErrors(errors, t);

  return (
    <form onSubmit={submitHandler}>
      <FormFields
        allowToggle={allowToggle}
        clearErrors={clearErrors}
        control={control}
        defaultMethod={defaultMethod}
        errors={errors}
        passwordInputType={passwordInputType}
        passwordLess={passwordLess}
        register={register}
        setError={setError}
        setShowPassword={setShowPassword}
        setValue={setValue}
        signInUrl={signInUrl}
        watchPassword={watchPassword}
      />
      <FormActions
        control={control}
        errors={errors}
        formErrors={formErrors}
        isSubmitting={isSubmitting}
        onUrgentHelpClick={onUrgentHelpClick}
        preSignUpText={preSignUpText}
      />
    </form>
  );
};

export { RegisterForm };
